import LanguageSwitcher from '@components/UI/LanguageSwitcher/LanguageSwitcher.component';
import { useTranslationHelper } from '@shared/hooks/useTranslationsHelper';
import {
  MdcBox,
  MdcButton,
  MdcCard,
  MdcCardContent,
  MdcGrid,
  MdcIcon,
  MdcTypography
} from 'ids-wc/dist/react/components';

import { useKeycloakAuth } from '@shared/hooks/useKeycloakAuth';
import { selectAppStoreUrls } from '@shared/store/app/app.selectors';
import { getStoreUrl } from '@utils/capacitor-utils';
import { useSelector } from 'react-redux';
import styles from './AccessDenied.module.css';

export type AccessDeniedReason = 'unregisteredImmigrants' | 'missingAFM' | 'generic' | 'mobileRequiresUpdate';

const ReasonTranslation: Record<AccessDeniedReason, string> = {
  generic: 'accessDeniedGeneric',
  unregisteredImmigrants: 'accessDeniedUnregisteredImmigrants',
  missingAFM: 'accessDeniedMissingAFM',
  mobileRequiresUpdate: 'accessDeniedMobileRequiresUpdate'
};

interface AccessDeniedProps {
  reason: AccessDeniedReason;
}

export const AccessDenied = (props: AccessDeniedProps) => {
  const { t } = useTranslationHelper('common');
  const { logout } = useKeycloakAuth();

  const { reason } = props;
  const appStoreUrls = useSelector(selectAppStoreUrls);

  const { t: transParentKey, exists } = useTranslationHelper(ReasonTranslation[reason]);

  const title = transParentKey('title');
  const text1 = transParentKey('text1');
  const text2 = transParentKey('text2');

  const handleUpdateApp = () => {
    if (appStoreUrls) {
      const storeUrl = getStoreUrl(appStoreUrls.ios, appStoreUrls.android);
      window.open(storeUrl, '_blank');
    }
  };

  return (
    <MdcBox className={styles['access-denied-container']}>
      <MdcGrid row>
        <MdcGrid col={12}>
          <MdcCard shape="rounded" background="surface-1">
            <MdcCardContent>
              <MdcGrid row>
                <MdcGrid col={6}>
                  <MdcBox display="inline-flex" justifyContent="center" className={styles['status-icon']}>
                    <MdcIcon title="warning-icon" color="error" icon="info-circle" />
                  </MdcBox>
                </MdcGrid>

                <MdcGrid col={6} className="d-flex justify-content-end">
                  <LanguageSwitcher changeUIOnly />
                </MdcGrid>
              </MdcGrid>

              <MdcGrid row className="mt-2 mb-2">
                <MdcGrid col={12}>
                  <MdcTypography textAlign="left" variant="headline4" color="textPrimary" text={title} />
                </MdcGrid>
              </MdcGrid>

              {exists('text1') && (
                <MdcGrid row className="mb-2">
                  <MdcGrid col={12}>
                    <MdcTypography textAlign="left" variant="body1" color="textPrimary" text={text1} />
                  </MdcGrid>
                </MdcGrid>
              )}

              {exists('text2') && (
                <MdcGrid row className="mb-2">
                  <MdcGrid col={12}>
                    <MdcTypography textAlign="left" variant="body1" color="textPrimary" text={text2} />
                  </MdcGrid>
                </MdcGrid>
              )}

              <MdcGrid row className="mt-2">
                <MdcGrid col={12}>
                  {reason === 'mobileRequiresUpdate' ? (
                    <MdcButton label={transParentKey('updateApp')} className="mt-2" onClick={handleUpdateApp} />
                  ) : (
                    <MdcButton label={t('logout')} className="mt-2" onClick={logout} />
                  )}
                </MdcGrid>
              </MdcGrid>
            </MdcCardContent>
          </MdcCard>
        </MdcGrid>
      </MdcGrid>
    </MdcBox>
  );
};
